import React, { type ReactNode, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { Col, Row } from 'react-flexbox-grid';
import { Button, Module, ModuleBody } from '@wfp/ui';
import { iconAddGlyph, iconArrows, iconCheckmarkGlyph, iconDocument } from '@wfp/icons';

import { AddSectionModal } from './AddSectionModal';
import { clearAllSections, createSection, orderSections } from '../../restapi/section';
import {
	type CreateSectionData,
	type  EmailRecipients,
	orderInDocNotAssigned,
	type Section,
	SectionState,
	Topic,
	topicsToOrder,
} from '../../restapi/types';
import { useCurrentEdition, useDedobUser, useDocId } from '../../hooks/home';
import {
	hasConfidentialContent,
	publish,
	userCanAddSection,
	userCanClearAll,
	userCanOrderSections,
	userCanPublish,
} from '../../restapi/document';
import { OrderModal } from './OrderModal';
import { PublishModal } from './PublishModal';
import confirm from '../common/dialogs/Confirm';

import './HomeActions.scss';


interface Props {
}

export function HomeActions(props: Props) {
	const user = useDedobUser();
	const docId = useDocId();
	const queryClient = useQueryClient();

	const ed = useCurrentEdition();
	const edHasHighlights = ed?.topicSectionSet.some(t =>
		(t.topic === Topic.highlights) && t.contentSet.length) || false;

	const edHasSections = (ed?.topicSectionSet || []).reduce((sum, t) => sum + t.contentSet.length, 0) > 0;
	const edReadyForPublish = edHasSections && ed?.topicSectionSet.every(
		t => t.contentSet.every(s => s.state === SectionState.cleared),
	);
	const requiresOrder = ed?.topicSectionSet.some(
		t => topicsToOrder.includes(t.topic) &&
			t.contentSet.some(s => s.orderInDoc === orderInDocNotAssigned),
	);

	const [isWorking, setIsWorking] = useState(false);

	// New Section
	const navigate = useNavigate();
	const sectionMutation = useMutation((secData: CreateSectionData) => createSection(docId, secData), {
		onSuccess: (id: number) => {
			queryClient.invalidateQueries('doc');
			navigate(`/section/${id}`);
		},
		onMutate: () => setIsWorking(true),
		onSettled: () => setIsWorking(false),
	});
	const [sectionModalOpen, setSectionModalOpen] = useState(false);

	// Clear all
	const clearAllMutation = useMutation(() => clearAllSections(docId), {
		onSuccess: () => queryClient.invalidateQueries('doc'),
		onError: (error: AxiosError) => {
			const json: any = error.response?.data;
			const serverMsg = '' + json?.message || 'Unspecified';
			let msg: ReactNode = <p>Clear all failed with message: {serverMsg}.</p>;
			if (serverMsg.toLowerCase().includes('section locked by another user')) {
				msg = <>{msg}
					<p className="mt2">If you are sure that all editing have been done, you can override the lock in the
						section editing page.</p>
				</>
			}
			confirm({message: msg});
		},
		onMutate: () => setIsWorking(true),
		onSettled: () => setIsWorking(false),
	});
	const clearAll = useCallback(
		() => {
			confirm({
				title: 'Clear all sections',
				message: `Do you want to clear all sections of current edition?`,
				okLabel: 'Clear all',
				cancelLabel: 'Cancel',
				type: 'warning',
			}).then(yes => {
				if (yes) {
					clearAllMutation.mutate();
				}
			});
		}, [clearAllMutation],
	);

	const canAddSection = user && !isWorking && userCanAddSection(user);
	const canClear = user && !isWorking && userCanClearAll(user);
	const canOrderSections = user && !isWorking && userCanOrderSections(user);
	const canPublish = user && !isWorking && userCanPublish(user);

	// Order
	const orderMutation = useMutation((sections: Section[]) => orderSections(docId, sections), {
		onSuccess: () => queryClient.invalidateQueries('doc'),
		onMutate: () => setIsWorking(true),
		onSettled: () => setIsWorking(false),
	});
	const [orderModalOpen, setOrderModalOpen] = useState(false);

	// Publish
	const publishMutation = useMutation((d: {
		addresses: EmailRecipients,
		when?: string
	}) => publish(docId, d.addresses, d.when), {
		onSuccess: () => queryClient.invalidateQueries('doc'),
		onError: (error: AxiosError) => {
			const json: any = error.response?.data;
			const serverMsg = json?.message || 'Unspecified';
			confirm({message: 'Publish failed with message:\n' + serverMsg});
		},
		onMutate: () => setIsWorking(true),
		onSettled: () => setIsWorking(false),
	});
	const [publishModalOpen, setPublishModalOpen] = useState(false);
	const publishCheck = useCallback(() => {
		if (!ed) {
			return;
		}
		if (hasConfidentialContent(ed)) {
			confirm({
				title: 'Confidential content',
				message: `Current Edition contains confidential content, do you really want to proceed?`,
				okLabel: 'Proceed',
				cancelLabel: 'Cancel',
				type: 'danger',
			}).then(yes => {
				if (yes) {
					setPublishModalOpen(true);
				}
			});
		} else {
			// proceed without confirmation
			setPublishModalOpen(true);
		}
	}, [ed]);

	return (
		<Module style={{margin: '0 -8px'}}>
			<ModuleBody>
				{sectionModalOpen &&
					<AddSectionModal
						disableHighlights={edHasHighlights}
						done={(secData) => {
							secData && sectionMutation.mutate(secData);
							setSectionModalOpen(false);
						}}/>
				}
				{orderModalOpen && <OrderModal
					done={(data) => {
						data && orderMutation.mutate(data);
						setOrderModalOpen(false);
					}}/>
				}
				{publishModalOpen && ed && <PublishModal
					edition={ed}
					done={(data, when) => {
						if (data) {
							publishMutation.mutate({addresses: data, when: when});
						}
						setPublishModalOpen(false);
					}}/>
				}
				<Row middle="xs" className="home-actions">
					<Col xs={12} sm={6} md={3} className="col">
						<Button disabled={!canAddSection} kind="primary"
								icon={iconAddGlyph} iconDescription=" " iconReverse
								onClick={() => setSectionModalOpen(true)}>New section</Button>
					</Col>
					<Col xs={12} sm={6} md={3} className="col">
						<Button disabled={!edHasSections || edReadyForPublish || !canClear} kind="accent"
								icon={iconCheckmarkGlyph} iconDescription=" " iconReverse
								onClick={() => clearAll()}>Clear all</Button>
					</Col>
					<Col xs={12} sm={6} md={3} className="col">
						<Button disabled={!edHasSections || !canOrderSections} kind="secondary"
								icon={iconArrows} iconDescription=" " iconReverse
								onClick={() => setOrderModalOpen(true)}>Order sections</Button>
					</Col>
					<Col xs={12} sm={6} md={3} className="col">
						<Button disabled={!edReadyForPublish || requiresOrder || !canPublish} kind="primary"
								icon={iconDocument} iconDescription=" " iconReverse
								onClick={() => publishCheck()}>Publish</Button>
					</Col>
				</Row>
			</ModuleBody>
		</Module>
	);
}